/**
 * <in-real-homes> — PDP "In real homes" gallery.
 * The carousel itself is Dawn's slider-component; this element adds the
 * full-size lightbox: open on card activation, prev/next (buttons + arrow
 * keys), and a position counter. Dismissal, focus trap, and focus return are
 * handled natively by the <dialog> element.
 */
class InRealHomes extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-irh-lightbox]');
    this.image = this.querySelector('[data-irh-lb-image]');
    this.counter = this.querySelector('[data-irh-counter]');
    if (!this.dialog || !this.image) return;

    this.triggers = Array.from(this.querySelectorAll('[data-irh-trigger]'));
    this.sources = this.triggers.map((trigger) => trigger.dataset.irhFull);
    this.index = 0;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this.open(Number(trigger.dataset.irhIndex) || 0);
      });
    });

    this.querySelector('[data-irh-close]')?.addEventListener('click', () => this.dialog.close());
    this.querySelector('[data-irh-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-irh-next]')?.addEventListener('click', () => this.step(1));

    // Close when the backdrop (the dialog element itself) is clicked.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.step(1);
      else if (event.key === 'ArrowLeft') this.step(-1);
    });
  }

  open(index) {
    this.index = index;
    this.render();
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
  }

  step(delta) {
    const count = this.sources.length;
    if (count < 2) return;
    this.index = (this.index + delta + count) % count;
    this.render();
  }

  render() {
    // Hide before swapping src so the previously-loaded image can't flash
    // while the new one decodes; reveal once it's ready.
    const reveal = () => {
      this.image.style.opacity = '1';
    };
    this.image.style.opacity = '0';
    this.image.src = this.sources[this.index] || '';
    if (typeof this.image.decode === 'function') {
      this.image.decode().then(reveal).catch(reveal);
    } else {
      this.image.addEventListener('load', reveal, { once: true });
    }

    if (this.counter) {
      const pad = (n) => String(n).padStart(2, '0');
      this.counter.textContent = `${pad(this.index + 1)} / ${pad(this.sources.length)}`;
    }
  }
}

if (!customElements.get('in-real-homes')) {
  customElements.define('in-real-homes', InRealHomes);
}
